import React, { useState, useEffect } from 'react';

const EducationAnalysis = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/analytics/education`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result);
      }
    } catch (error) {
      console.error('Error fetching education data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    const handleRefresh = () => {
      setLoading(true);
      fetchData();
    };
    
    window.addEventListener('analyticsRefresh', handleRefresh);
    return () => window.removeEventListener('analyticsRefresh', handleRefresh);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white p-6 rounded-lg shadow animate-pulse">
            <div className="h-6 bg-gray-200 rounded mb-4 w-1/3"></div>
            <div className="h-64 bg-gray-200 rounded"></div>
          </div>
        ))}
      </div>
    );
  }

  const renderGpaDistribution = () => {
    if (!data?.gpaDistribution?.length) return null;

    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">GPA Distribution</h3>
        <div className="space-y-3">
          {data.gpaDistribution.map((bucket, index) => {
            const total = data.gpaDistribution.reduce((sum, b) => sum + b.count, 0);
            const percentage = ((bucket.count / total) * 100).toFixed(1);
            const rangeLabel = typeof bucket._id === 'object' 
              ? `${bucket._id.min || 0} - ${bucket._id.max || 'N/A'}`
              : bucket._id;

            return (
              <div key={index} className="flex items-center justify-between">
                <div className="flex items-center flex-1">
                  <span className="w-24 text-sm font-medium">{rangeLabel}</span>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 rounded-full h-4">
                      <div 
                        className="bg-blue-600 h-4 rounded-full flex items-center justify-center text-xs text-white font-medium" 
                        style={{ width: `${percentage}%` }}
                      >
                        {percentage > 10 && `${percentage}%`}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-sm font-medium">{bucket.count}</span>
                    {bucket.avgGpa && (
                      <span className="text-xs text-gray-500 ml-2">
                        (avg: {bucket.avgGpa.toFixed(2)})
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Education Levels */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Education Levels</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {data?.levels?.map((level, index) => (
            <div key={index} className="text-center p-4 bg-gray-50 rounded-lg">
              <p className="text-2xl font-bold text-blue-600">{level.count}</p>
              <p className="text-sm text-gray-600 capitalize">{level._id}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Top Institutions */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Top Institutions</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institution
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Students
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data?.institutions?.slice(0, 10).map((institution, index) => (
                <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    #{index + 1}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {institution._id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {institution.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Departments */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold mb-4">Top Departments</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data?.departments?.slice(0, 12).map((dept, index) => {
            const maxCount = data.departments[0]?.count || 1;
            const percentage = (dept.count / maxCount) * 100;
            
            return (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{dept._id}</p>
                  <div className="mt-1 w-full bg-gray-200 rounded-full h-2">
                    <div 
                      className="bg-green-600 h-2 rounded-full" 
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <span className="ml-3 text-sm font-medium text-gray-900">{dept.count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* GPA Distribution */}
      {renderGpaDistribution()}
    </div>
  );
};

export default EducationAnalysis;